import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ChevronDown, Bookmark, MapPin, History, BarChart3, Menu, Home, HelpCircle, LogOut, LogIn, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const Navbar = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      console.log('[Navbar] Initial session:', session?.user?.id, 'isAnonymous:', session?.user?.is_anonymous);
      setUser(session?.user ?? null);
      setIsAnonymous(session?.user?.is_anonymous === true);
    };
    
    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Navbar] Auth state change:', event, 'user:', session?.user?.id, 'isAnonymous:', session?.user?.is_anonymous);
      setUser(session?.user ?? null);
      setIsAnonymous(session?.user?.is_anonymous === true);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleSignOut = async () => {
    console.log('[Navbar] Signing out...');
    // Immediately update UI state
    setUser(null);
    setIsAnonymous(false);
    setMobileMenuOpen(false);
    
    // Clear any legacy pending verification data
    localStorage.removeItem('pending_signin_email');
    localStorage.removeItem('pending_signin_password');
    localStorage.removeItem('pending_anonymous_migration');
    
    try {
      // Use scope: 'local' to ensure only this tab's session is cleared
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        console.error('[Navbar] Sign out error:', error);
      } else {
        console.log('[Navbar] Sign out successful');
      }
    } catch (err) {
      console.error('[Navbar] Sign out exception:', err);
    }
    
    // Always navigate to home after logout attempt
    navigate('/');
  };

  const handleNavigate = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  // Show profile access if user is authenticated and not anonymous
  const showProfileAccess = user && !isAnonymous;

  return (
    <header className="border-b border-border bg-background/80 backdrop-blur-sm sticky top-0 z-50">
      <nav className="container mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <img src="/logo-v2-b.webp" alt="TripVerse logo" className="h-8 w-8" width="32" height="32" />
          <span className="text-xl font-bold text-primary">
            TripVerse
          </span>
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm">Home</Button>
          </Link>
          <Link to="/quiz">
            <Button variant="accent" size="sm">Quiz</Button>
          </Link>
          
          {showProfileAccess ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-1">
                    <User className="h-4 w-4 mr-1" />
                    Profile
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-background">
                  <DropdownMenuItem onClick={() => navigate('/profile?tab=saved')}>
                    <Bookmark className="mr-2 h-4 w-4" />
                    Saved
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile?tab=visited')}>
                    <MapPin className="mr-2 h-4 w-4" />
                    Visited
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile?tab=history')}>
                    <History className="mr-2 h-4 w-4" />
                    History
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile?tab=stats')}>
                    <BarChart3 className="mr-2 h-4 w-4" />
                    My Stats
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="ghost" onClick={handleSignOut}>
                Logout
              </Button>
            </>
          ) : (
            <Link to="/auth">
              <Button variant="outline">Login</Button>
            </Link>
          )}
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="flex flex-col gap-2 mt-8">
                <Button 
                  variant="ghost" 
                  className="justify-start gap-3 h-12"
                  onClick={() => handleNavigate('/')}
                >
                  <Home className="h-5 w-5" />
                  Home
                </Button>
                <Button 
                  variant="accent" 
                  className="justify-start gap-3 h-12"
                  onClick={() => handleNavigate('/quiz')}
                >
                  <HelpCircle className="h-5 w-5" />
                  Take Quiz
                </Button>
                
                {showProfileAccess ? (
                  <>
                    <div className="border-t border-border my-2" />
                    <p className="text-xs text-muted-foreground px-4 py-2 uppercase tracking-wider">
                      Profile
                    </p>
                    <Button 
                      variant="ghost" 
                      className="justify-start gap-3 h-12"
                      onClick={() => handleNavigate('/profile?tab=saved')}
                    >
                      <Bookmark className="h-5 w-5" />
                      Saved
                    </Button>
                    <Button 
                      variant="ghost" 
                      className="justify-start gap-3 h-12"
                      onClick={() => handleNavigate('/profile?tab=visited')}
                    >
                      <MapPin className="h-5 w-5" />
                      Visited
                    </Button>
                    <Button 
                      variant="ghost" 
                      className="justify-start gap-3 h-12"
                      onClick={() => handleNavigate('/profile?tab=history')}
                    >
                      <History className="h-5 w-5" />
                      History
                    </Button>
                    <Button 
                      variant="ghost" 
                      className="justify-start gap-3 h-12"
                      onClick={() => handleNavigate('/profile?tab=stats')}
                    >
                      <BarChart3 className="h-5 w-5" />
                      My Stats
                    </Button>
                    <div className="border-t border-border my-2" />
                    <Button 
                      variant="ghost" 
                      className="justify-start gap-3 h-12 text-destructive hover:text-destructive"
                      onClick={handleSignOut}
                    >
                      <LogOut className="h-5 w-5" />
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="border-t border-border my-2" />
                    <Button 
                      variant="outline" 
                      className="justify-start gap-3 h-12"
                      onClick={() => handleNavigate('/auth')}
                    >
                      <LogIn className="h-5 w-5" />
                      Login
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;